/**
 * AudioManager — background music + game SFX, all synthesised via Web Audio API.
 *
 * Two independent output buses:
 *   musicBus  — gentle chord progression (C→Am→F→G) + bird chirps.
 *               Silenced by the speaker / mute button.
 *   sfxBus    — drop swoosh, settling thud, tower-fall crash.
 *               Always audible regardless of the mute button.
 *
 * Usage:
 *   import audioManager from './AudioManager';
 *   audioManager.ensureStarted();       // call inside a user-gesture handler
 *   audioManager.setMuted(true/false);  // mutes music only
 *   audioManager.soundDrop();
 *   audioManager.soundSlide();          // auto-throttled internally
 *   audioManager.soundTowerFall();
 */

const CHORDS = [
  [261.63, 329.63, 392.00],
  [220.00, 261.63, 329.63],
  [174.61, 220.00, 261.63],
  [196.00, 246.94, 293.66],
];
const CHORD_DUR = 7;
const ATTACK    = 2.0;
const RELEASE   = 2.0;
const NOTE_VOL  = 0.06;
const LPF_FREQ  = 900;

class AudioManager {
  constructor() {
    this.ctx        = null;
    this.musicBus   = null;   // gain node for background music
    this.sfxBus     = null;   // gain node for SFX (never muted)
    this._started   = false;
    this._lastSlide = 0;      // ms timestamp — throttle the slide sound
    this.isMuted    = localStorage.getItem('animalstack_muted') === 'true';
    this.isSfxMuted = localStorage.getItem('animalstack_sfx_muted') === 'true';

    // MP3 sound buffers
    this.dropSoundBuffer = null;

    document.addEventListener('visibilitychange', () => {
      if (!this.ctx) return;
      if (document.hidden)      this.ctx.suspend();
      else if (this._started)   this.ctx.resume();
    });
  }

  // ── Public API ────────────────────────────────────────────────────────────

  ensureStarted() {
    if (!this._started) {
      this._started = true;
      this._init();
    } else if (this.ctx?.state === 'suspended') {
      this.ctx.resume();
    }
  }

  setMuted(muted) {
    this.isMuted = muted;
    localStorage.setItem('animalstack_muted', muted ? 'true' : 'false');
    if (this.musicBus && this.ctx) {
      this.musicBus.gain.setTargetAtTime(muted ? 0 : 1.0, this.ctx.currentTime, 0.06);
    }
  }

  setSfxMuted(muted) {
    this.isSfxMuted = muted;
    localStorage.setItem('animalstack_sfx_muted', muted ? 'true' : 'false');
    if (this.sfxBus && this.ctx) {
      this.sfxBus.gain.setTargetAtTime(muted ? 0 : 0.85, this.ctx.currentTime, 0.06);
    }
  }

  // ── Sound Loading ────────────────────────────────────────────────────────

  /**
   * Load the ka-ching MP3 file and decode it into an AudioBuffer.
   * Place your MP3 at: assets/sounds/ka-ching.mp3
   */
  async loadDropSound() {
    try {
      const response = await fetch('./assets/sounds/ka-ching.mp3');
      if (!response.ok) {
        console.warn('ka-ching.mp3 not found, using synthesized fallback');
        return;
      }
      const arrayBuffer = await response.arrayBuffer();
      this.dropSoundBuffer = await this.ctx.decodeAudioData(arrayBuffer);
      console.log('ka-ching.mp3 loaded successfully');
    } catch (e) {
      console.warn('Failed to load ka-ching sound, using synthesized fallback:', e);
    }
  }

  // ── SFX ──────────────────────────────────────────────────────────────────

  /** Ka-ching cash register sound when an animal is dropped. */
  soundDrop() {
    const ctx = this.ctx;
    if (!ctx || ctx.state !== 'running') return;

    // If MP3 is loaded, play it
    if (this.dropSoundBuffer) {
      const source = ctx.createBufferSource();
      source.buffer = this.dropSoundBuffer;

      // Optional: add a gain node to control volume
      const gainNode = ctx.createGain();
      gainNode.gain.value = 0.7; // Adjust volume (0.0 to 1.0)

      source.connect(gainNode);
      gainNode.connect(this.sfxBus);
      source.start();
      return;
    }

    // ── Fallback: Synthesized ka-ching if MP3 not loaded ──────────────────
    const now = ctx.currentTime;

    // "Ka" — mechanical thud at t=0
    const thudOsc = ctx.createOscillator();
    thudOsc.type = 'sine';
    thudOsc.frequency.setValueAtTime(180, now);
    thudOsc.frequency.exponentialRampToValueAtTime(60, now + 0.06);
    const thudGn = ctx.createGain();
    thudGn.gain.setValueAtTime(0.22, now);
    thudGn.gain.exponentialRampToValueAtTime(0.001, now + 0.06);
    thudOsc.connect(thudGn); thudGn.connect(this.sfxBus);
    thudOsc.start(now); thudOsc.stop(now + 0.07);

    // Mechanical click noise burst at t=0
    const clickLen = Math.floor(ctx.sampleRate * 0.018);
    const clickBuf = ctx.createBuffer(1, clickLen, ctx.sampleRate);
    const clickData = clickBuf.getChannelData(0);
    for (let i = 0; i < clickLen; i++) clickData[i] = Math.random() * 2 - 1;
    const clickSrc = ctx.createBufferSource();
    clickSrc.buffer = clickBuf;
    const clickHp = ctx.createBiquadFilter();
    clickHp.type = 'highpass'; clickHp.frequency.value = 1800;
    const clickGn = ctx.createGain();
    clickGn.gain.setValueAtTime(0.14, now);
    clickGn.gain.exponentialRampToValueAtTime(0.001, now + 0.018);
    clickSrc.connect(clickHp); clickHp.connect(clickGn); clickGn.connect(this.sfxBus);
    clickSrc.start(now);

    // "Ching" — bright metallic bell ring at t=0.05
    const chingStart = now + 0.05;
    [2637, 3951, 5274].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const gn = ctx.createGain();
      const vol = 0.13 - i * 0.03;
      gn.gain.setValueAtTime(vol, chingStart);
      gn.gain.exponentialRampToValueAtTime(0.001, chingStart + 0.55);
      osc.connect(gn); gn.connect(this.sfxBus);
      osc.start(chingStart); osc.stop(chingStart + 0.6);
    });

    // Metallic shimmer — filtered noise ring on top of the ching
    const shimLen = Math.floor(ctx.sampleRate * 0.55);
    const shimBuf = ctx.createBuffer(1, shimLen, ctx.sampleRate);
    const shimData = shimBuf.getChannelData(0);
    for (let i = 0; i < shimLen; i++) shimData[i] = Math.random() * 2 - 1;
    const shimSrc = ctx.createBufferSource();
    shimSrc.buffer = shimBuf;
    const shimBp = ctx.createBiquadFilter();
    shimBp.type = 'bandpass'; shimBp.frequency.value = 4200; shimBp.Q.value = 4;
    const shimGn = ctx.createGain();
    shimGn.gain.setValueAtTime(0.07, chingStart);
    shimGn.gain.exponentialRampToValueAtTime(0.001, chingStart + 0.55);
    shimSrc.connect(shimBp); shimBp.connect(shimGn); shimGn.connect(this.sfxBus);
    shimSrc.start(chingStart);
  }

  /**
   * Gentle settling thud — call whenever animals are visibly sliding.
   * Internally throttled to at most once every 550 ms.
   */
  soundSlide() {
    const now = Date.now();
    if (now - this._lastSlide < 550) return;
    this._lastSlide = now;

    const ctx = this.ctx;
    if (!ctx || ctx.state !== 'running') return;
    const t = ctx.currentTime;

    // Soft low thump: 160 Hz sine, 110 ms
    const osc = ctx.createOscillator();
    osc.type  = 'sine';
    osc.frequency.setValueAtTime(160, t);
    osc.frequency.exponentialRampToValueAtTime(80, t + 0.11);

    const gn = ctx.createGain();
    gn.gain.setValueAtTime(0, t);
    gn.gain.linearRampToValueAtTime(0.09, t + 0.02);
    gn.gain.exponentialRampToValueAtTime(0.001, t + 0.11);

    osc.connect(gn); gn.connect(this.sfxBus);
    osc.start(t);    osc.stop(t + 0.13);

    // Very faint mid-frequency rustle underneath
    const bufLen = Math.floor(ctx.sampleRate * 0.09);
    const buf    = ctx.createBuffer(1, bufLen, ctx.sampleRate);
    const d      = buf.getChannelData(0);
    for (let i = 0; i < bufLen; i++) d[i] = Math.random() * 2 - 1;

    const src  = ctx.createBufferSource();
    src.buffer = buf;
    const bp   = ctx.createBiquadFilter();
    bp.type    = 'bandpass'; bp.frequency.value = 420; bp.Q.value = 1.2;
    const rgn  = ctx.createGain();
    rgn.gain.setValueAtTime(0.055, t);
    rgn.gain.exponentialRampToValueAtTime(0.001, t + 0.09);

    src.connect(bp); bp.connect(rgn); rgn.connect(this.sfxBus);
    src.start(t);
  }

  /** Rumbling crash when the tower falls. */
  soundTowerFall() {
    const ctx = this.ctx;
    if (!ctx || ctx.state !== 'running') return;
    const now = ctx.currentTime;

    // Three low oscillators staggered slightly — the "rumble"
    [{ f: 90, t: 0.00, vol: 0.17 },
     { f: 58, t: 0.04, vol: 0.15 },
     { f: 72, t: 0.08, vol: 0.13 }].forEach(({ f, t, vol }) => {
      const osc = ctx.createOscillator();
      osc.type  = 'sine';
      osc.frequency.setValueAtTime(f,         now + t);
      osc.frequency.exponentialRampToValueAtTime(f * 0.35, now + t + 0.75);

      const gn = ctx.createGain();
      gn.gain.setValueAtTime(0,   now + t);
      gn.gain.linearRampToValueAtTime(vol,   now + t + 0.035);
      gn.gain.exponentialRampToValueAtTime(0.001, now + t + 0.75);

      osc.connect(gn); gn.connect(this.sfxBus);
      osc.start(now + t); osc.stop(now + t + 0.8);
    });

    // Low-pass noise burst for the "crash" texture
    const bufLen = Math.floor(ctx.sampleRate * 0.45);
    const buf    = ctx.createBuffer(1, bufLen, ctx.sampleRate);
    const d      = buf.getChannelData(0);
    for (let i = 0; i < bufLen; i++) d[i] = Math.random() * 2 - 1;

    const src  = ctx.createBufferSource();
    src.buffer = buf;
    const lp   = ctx.createBiquadFilter();
    lp.type    = 'lowpass'; lp.frequency.value = 700;
    const cgn  = ctx.createGain();
    cgn.gain.setValueAtTime(0.16, now);
    cgn.gain.exponentialRampToValueAtTime(0.001, now + 0.45);

    src.connect(lp); lp.connect(cgn); cgn.connect(this.sfxBus);
    src.start(now);
  }

  // ── Private ───────────────────────────────────────────────────────────────

  _init() {
    this.ctx = new (window.AudioContext || window.webkitAudioContext)();

    // Music bus — mutable
    this.musicBus = this.ctx.createGain();
    this.musicBus.gain.value = this.isMuted ? 0 : 1.0;
    this.musicBus.connect(this.ctx.destination);

    // SFX bus
    this.sfxBus = this.ctx.createGain();
    this.sfxBus.gain.value = this.isSfxMuted ? 0 : 0.85;
    this.sfxBus.connect(this.ctx.destination);

    // Load MP3 sounds first, then start music
    this.loadDropSound().then(() => {
      this._scheduleChord(0, this.ctx.currentTime + 0.5);
      this._scheduleBird();
    });
  }

  _scheduleChord(idx, startTime) {
    const freqs = CHORDS[idx % CHORDS.length];

    freqs.forEach(freq => {
      [-4, +4].forEach(detune => {
        const osc = this.ctx.createOscillator();
        osc.type  = 'triangle';
        osc.frequency.value = freq;
        osc.detune.value    = detune;

        const lpf = this.ctx.createBiquadFilter();
        lpf.type  = 'lowpass'; lpf.frequency.value = LPF_FREQ;

        const gn = this.ctx.createGain();
        gn.gain.setValueAtTime(0,             startTime);
        gn.gain.linearRampToValueAtTime(NOTE_VOL * 0.5, startTime + ATTACK);
        gn.gain.setValueAtTime(NOTE_VOL * 0.5,          startTime + CHORD_DUR - RELEASE);
        gn.gain.linearRampToValueAtTime(0,               startTime + CHORD_DUR);

        osc.connect(lpf); lpf.connect(gn); gn.connect(this.musicBus);
        osc.start(startTime); osc.stop(startTime + CHORD_DUR + 0.1);
      });
    });

    const nextStart = startTime + CHORD_DUR - RELEASE;
    setTimeout(() => {
      if (this._started) this._scheduleChord(idx + 1, nextStart);
    }, Math.max(0, (nextStart - this.ctx.currentTime) * 1000 - 80));
  }

  _scheduleBird() {
    const delay = 4000 + Math.random() * 9000;
    setTimeout(() => {
      if (this._started && this.ctx?.state === 'running') this._chirp();
      this._scheduleBird();
    }, delay);
  }

  _chirp() {
    const ctx      = this.ctx;
    const now      = ctx.currentTime;
    const count    = 1 + Math.floor(Math.random() * 3);
    const baseFreq = 1800 + Math.random() * 1200;

    for (let i = 0; i < count; i++) {
      const t   = now + i * 0.14;
      const dur = 0.07 + Math.random() * 0.09;

      const osc = ctx.createOscillator();
      osc.type  = 'sine';
      osc.frequency.setValueAtTime(baseFreq, t);
      osc.frequency.linearRampToValueAtTime(baseFreq * (1.2 + Math.random() * 0.4), t + dur * 0.5);
      osc.frequency.linearRampToValueAtTime(baseFreq * 0.9, t + dur);

      const gn = ctx.createGain();
      gn.gain.setValueAtTime(0, t);
      gn.gain.linearRampToValueAtTime(0.12, t + dur * 0.3);
      gn.gain.linearRampToValueAtTime(0,    t + dur);

      osc.connect(gn); gn.connect(this.musicBus);
      osc.start(t); osc.stop(t + dur + 0.02);
    }
  }
}

const audioManager = new AudioManager();
export default audioManager;
